// Page publique d'acceptation d'invitation (Lot 6B).
//
// Flow :
// 1) useEffect → GET /api/invitations/:token pour charger les métadonnées
//    (tenant, rôle, inviter, expiration)
// 2) Si pending + non expirée → on affiche le formulaire (mot de passe +
//    nom). L'email est fixe (celui de l'invitation).
// 3) POST /api/invitations/:token/accept → crée le user + membership,
//    nous renvoie un AuthResponse. On hydrate le store puis on redirige
//    vers /.
//
// Cas limites visibles à l'utilisateur :
// - Token inconnu → 404 → "Invitation introuvable"
// - Invitation acceptée / révoquée / expirée → 410 → message explicite
// - Un user existe déjà pour cet email → 409 → on propose /login
//
// La route n'est PAS wrappée dans ProtectedRoute : c'est intentionnellement
// accessible sans être connecté, puisque c'est précisément l'objectif.

import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError } from "../api/client";
import {
  invitationsApi,
  type InvitationPreview,
} from "../api/invitations";
import { useAuthStore } from "../stores/auth";
import { AuthShell, Field } from "./LoginPage";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; preview: InvitationPreview }
  | { kind: "error"; status: number | null; message: string };

function roleLabel(role: InvitationPreview["role"]): string {
  switch (role) {
    case "admin":
      return "Administrateur";
    case "comptable":
      return "Comptable";
    case "viewer":
      return "Observateur";
  }
}

export function AcceptInvitationPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        status: 404,
        message: "Invitation introuvable",
      });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const preview = await invitationsApi.preview(token);
        if (cancelled) return;
        setState({ kind: "ready", preview });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setState({
            kind: "error",
            status: err.status,
            message: err.detail,
          });
        } else {
          setState({
            kind: "error",
            status: null,
            message: "Impossible de charger l'invitation",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      const resp = await invitationsApi.accept(token, {
        password,
        full_name: fullName,
      });
      // Hydrate le store — on reprend exactement la forme de l'AuthResponse
      // qu'on reçoit aussi sur /signup et /login.
      const current = resp.current_tenant;
      const role =
        resp.memberships.find(
          (m) => current && m.tenant.id === current.id,
        )?.role ?? null;
      useAuthStore.setState({
        user: resp.user,
        memberships: resp.memberships,
        currentTenant: current,
        currentRole: role,
        accessToken: resp.tokens.access_token,
        refreshToken: resp.tokens.refresh_token,
        accessExpiresAt: resp.tokens.expires_at,
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setSubmitError(err.detail);
      else setSubmitError("Impossible d'accepter l'invitation");
    } finally {
      setSubmitting(false);
    }
  }

  if (state.kind === "loading") {
    return (
      <AuthShell title="Invitation" subtitle="HInvoice">
        <p className="text-sm text-slate-500">Chargement de l'invitation…</p>
      </AuthShell>
    );
  }

  if (state.kind === "error") {
    const title =
      state.status === 404
        ? "Invitation introuvable"
        : state.status === 410
          ? "Invitation indisponible"
          : "Erreur";
    return (
      <AuthShell title={title} subtitle="HInvoice">
        <p className="text-sm text-slate-600">{state.message}</p>
        <p className="text-sm text-slate-500 mt-4">
          Déjà un compte ?{" "}
          <Link to="/login" className="text-brand-600 hover:underline">
            Se connecter
          </Link>
        </p>
      </AuthShell>
    );
  }

  const { preview } = state;

  // Si le user existe déjà, on ne propose pas le formulaire de création —
  // on redirige vers /login. Le backend ajoute une couche de sécurité en
  // renvoyant 409, mais autant rendre l'UI explicite ici.
  if (preview.user_exists) {
    return (
      <AuthShell title="Compte déjà existant" subtitle="HInvoice">
        <p className="text-sm text-slate-600">
          Un compte existe déjà pour <strong>{preview.email}</strong>.
          Demandez à <strong>{preview.inviter_name ?? "l'administrateur"}</strong> de
          vous ajouter directement au tenant <strong>{preview.tenant_name}</strong>.
        </p>
        <p className="text-sm text-slate-500 mt-4">
          <Link to="/login" className="text-brand-600 hover:underline">
            Se connecter
          </Link>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={`Rejoindre ${preview.tenant_name}`}
      subtitle="HInvoice"
    >
      <div className="mb-5 text-sm text-slate-600 space-y-1">
        <p>
          <strong>{preview.inviter_name ?? "Un administrateur"}</strong> vous
          invite à rejoindre <strong>{preview.tenant_name}</strong> en tant
          que <strong>{roleLabel(preview.role).toLowerCase()}</strong>.
        </p>
        <p className="text-xs text-slate-400">
          Cette invitation est liée à <strong>{preview.email}</strong> et expire
          le{" "}
          {new Date(preview.expires_at).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })}
          .
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" aria-label="accept-form">
        <Field label="Email" htmlFor="accept_email">
          <input
            id="accept_email"
            type="email"
            value={preview.email}
            readOnly
            disabled
            className="input bg-slate-100 text-slate-600"
          />
        </Field>
        <Field label="Votre nom complet" htmlFor="accept_full_name">
          <input
            id="accept_full_name"
            required
            autoComplete="name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="input"
          />
        </Field>
        <Field
          label="Mot de passe"
          htmlFor="accept_password"
          hint="Minimum 10 caractères"
        >
          <input
            id="accept_password"
            type="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
          />
        </Field>
        {submitError && (
          <p role="alert" className="text-sm text-red-600">
            {submitError}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full"
        >
          {submitting ? "Création du compte…" : "Créer mon compte et rejoindre"}
        </button>
      </form>
    </AuthShell>
  );
}
